// Shared date utilities used by Board and Dashboard

export const TODAY = (() => { const d = new Date(); d.setHours(0,0,0,0); return d })()

export function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r }

export function dk(d) { const x = new Date(d); x.setHours(0,0,0,0); return x.toDateString() }

export function isToday(d) { return dk(d) === dk(TODAY) }

export function fmtDate(d) { return (d.getMonth() + 1) + '/' + d.getDate() }

export function fmtDow(d) { return d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2) }

export function fmtFull(d) { return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) }

// Expand a start date + "days onsite" into the actual calendar days worked.
// Saturday and Sunday are each included only if their flag is set, so a PM can
// take Saturdays but not Sundays. If the start date itself falls on an excluded
// day, the span begins on the next included day.
export function spanDays(start, daysOnsite, opts = {}) {
  const sat = !!opts.includeSaturday
  const sun = !!opts.includeSunday
  const want = Math.max(1, Math.min(30, parseInt(daysOnsite) || 1))
  const out = []
  let cur = new Date(start); cur.setHours(0, 0, 0, 0)
  let guard = 0
  // Guard is pure headroom: weekdays are always included, so the widest possible
  // span is 30 weekdays with both boxes off (~40 calendar days). It exists only so
  // a future rule that excludes more days can never spin here.
  while (out.length < want && guard < 250) {
    const dow = cur.getDay()          // 0 = Sun, 6 = Sat
    const ok = dow === 0 ? sun : dow === 6 ? sat : true
    if (ok) out.push(new Date(cur))
    cur = addDays(cur, 1); guard++
  }
  return out
}

// "Mon 8/17 – Wed 8/19 · 3 days" (or "Mon 8/17 · 1 day" for a single day)
export function fmtSpanLabel(days) {
  if (!days || days.length === 0) return ''
  const a = days[0], b = days[days.length - 1]
  const one = d => `${fmtDow(d)} ${fmtDate(d)}`
  return days.length === 1
    ? `${one(a)} · 1 day`
    : `${one(a)} – ${one(b)} · ${days.length} days`
}

// "Mon–Fri only" / "incl. Saturdays" / "incl. Sundays" / "incl. Sat + Sun"
export function fmtWeekendPolicy(includeSaturday, includeSunday) {
  if (includeSaturday && includeSunday) return 'incl. Sat + Sun'
  if (includeSaturday) return 'incl. Saturdays'
  if (includeSunday) return 'incl. Sundays'
  return 'Mon–Fri only'
}

export function fmtPhone(p) {
  if (!p) return ''
  const d = String(p).replace(/\D/g, '')
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`
  if (d.length === 11 && d[0] === '1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`
  return String(p)
}

// "Monday July 13th" — long weekday + month + ordinal day, built from the parts
// of a plain 'YYYY-MM-DD' date so the calendar day never shifts (no UTC parse).
export function fmtDowLong(ymd) {
  if (!ymd) return ''
  const [y, m, d] = String(ymd).split('-').map(Number)
  if (!y || !m || !d) return String(ymd)
  const dt = new Date(y, m - 1, d)
  const wd = dt.toLocaleDateString('en-US', { weekday: 'long' })
  const mo = dt.toLocaleDateString('en-US', { month: 'long' })
  const s = ['th', 'st', 'nd', 'rd'], v = d % 100
  const ord = d + (s[(v - 20) % 10] || s[v] || s[0])
  return `${wd} ${mo} ${ord}`
}

export function fmtLastWorked(dateStr) {
  if (!dateStr) return ''
  const parts = dateStr.split('-')
  if (parts.length !== 3) return dateStr
  const d = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]))
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Eastern ("America/New_York") calendar helpers for the daily field report flow.
// Both return a zero-padded 'YYYY-MM-DD' string (the 'en-CA' locale formats that
// way) for safe lexicographic comparison against report_date. Never feed these —
// or report_date — into new Date('YYYY-MM-DD') (the UTC-shift trap).
export function etToday() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
}
export function etDayOf(isoTimestamp) {
  if (!isoTimestamp) return null
  return new Date(isoTimestamp).toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
}

export function isOutOnDate(emp, dateObj) {
  if (!emp.timeOff || emp.timeOff.length === 0) return false
  return emp.timeOff.some(t => {
    const [fy, fm, fd] = t.from.split('-').map(Number)
    const [ty, tm, td] = t.to.split('-').map(Number)
    const from = new Date(fy, fm - 1, fd)
    const to = new Date(ty, tm - 1, td)
    from.setHours(0, 0, 0, 0)
    to.setHours(23, 59, 59, 999)
    return dateObj >= from && dateObj <= to
  })
}
